import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import {
    Departure,
    DetailedRoute,
    DetailedStop,
    Geometry,
    Itinerary,
    Leg,
    LegProduct,
    Pattern,
    Plan,
    Route,
    RouteGroups,
    RoutePatternStops,
    SearchRecentlyJourney,
    Stop,
    StopMarker,
    StopTime,
    Time,
    TransportMode,
    TripDetails,
    TripDetailsTime
} from './open-trip-planner.model';
import { catchError, defaultIfEmpty, finalize, map, mergeMap, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { AppStartupService, DisruptionMessage, DisruptionMessageResponse, LatLng, NetworkService } from '@ridango/core';
import { OtpUtil } from './open-trip-planner.util';
import Env from '@ridango/core/env';
import { isEqual } from 'lodash';
import { JsonUtil } from '@ridango/core/lib/utils/json-util';
import { Temporal } from '@js-temporal/polyfill';
import { AlertsService } from '@ridango/core/lib/services/alerts.service';
import { getDistance } from 'geolib';
import { DatePipe } from '@angular/common';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { getDefaultValue, JourneyPlannerFilter } from './journey-planner-filter.model';

const searchRecentlyJourneysKey = 'searchRecentlyJourneys';
export interface ScheduleParams {
    pattern: Pattern;
    stopId: string;
    scheduleDate: string;
}

@Injectable({
    providedIn: 'root'
})
export class OpenTripPlannerService {
    private allRoutesMemory: Map<string, Route> = new Map<string, Route>();
    private allAlertsMemory: DisruptionMessage[] = [];
    private allStopsMemory: Map<string, DetailedStop> = new Map<string, DetailedStop>();
    private routeIdToRouteMemory: Map<string, DetailedRoute> = new Map<string, DetailedRoute>();
    private routeIdInFlight: Map<string, Observable<DetailedRoute>> = new Map();
    private routeIdToPatternsMemory: Map<string, Pattern[]> = new Map<string, Pattern[]>();
    private patternIdToStopsMemory: Map<string, Stop[]> = new Map<string, Stop[]>();
    private stopNameToStopLocationsMemory: Map<string, Stop> = new Map<string, Stop>();
    private patternIdToPatternMemory: Map<string, Pattern> = new Map<string, Pattern>();
    private patternIdToPatternDescriptionMemory: Map<string, string> = new Map<string, string>();
    private TIME_FORMAT = this.env.PRIMARY_TIME_FORMAT || 'HH:mm';

    constructor(
        @Inject('ENV') public env: Env,
        private http: HttpClient,
        private appStartupService: AppStartupService,
        private alertsService: AlertsService,
        private datePipe: DatePipe,
        private network: NetworkService,
        private analyticsService: AnalyticsService
    ) {}

    private isFeatureNotAvailableInWMB(feature: string): boolean {
        return (
            this.env.WMB?.DONT_USE_FOR?.includes(feature.toUpperCase()) ||
            this.env.WMB?.DONT_USE_FOR?.some((feat: string) => feature.toUpperCase().includes(feat))
        );
    }

    getJourneyLegPrice(
        routeId: string,
        startZoneId: string,
        endZoneId: string,
        routeShortName: string
    ): Observable<LegProduct> {
        const routeIdNoPrefix = routeId.replace(`${this.env.OTP_INDEX}:`, '');

        let params = new HttpParams();
        params = params.append('routeId', routeIdNoPrefix);
        params = params.append('startZoneId', startZoneId);
        params = params.append('endZoneId', endZoneId);
        params = params.append('routeShortName', routeShortName);

        const url = `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/product/journey-leg/price`;

        return this.http.get<LegProduct>(url, { params: params }).pipe(
            catchError((error) => {
                console.error('Error fetching journey leg price:', error);
                throw error;
            })
        );
    }

    private getExploreUrl(suffix: string): string {
        if (this.isFeatureNotAvailableInWMB(suffix)) {
            return `${this.env.OTP_BASE_URL}/routers/1/index/${suffix}`;
        }

        return this.env.WMB
            ? `${this.env.WMB.WMB_PUBLIC_API}/v1/${suffix}`
            : `${this.env.OTP_BASE_URL}/routers/1/index/${suffix}`;
    }

    private getJourneyUrl(suffix: string): string {
        if (this.isFeatureNotAvailableInWMB(suffix)) {
            return `${this.env.OTP_BASE_URL}/routers/1/${suffix}`;
        }

        return this.env.WMB
            ? `${this.env.WMB.WMB_PUBLIC_API}/v1/journey/${suffix}`
            : `${this.env.OTP_BASE_URL}/routers/1/${suffix}`;
    }

    getAllAlerts(): Observable<DisruptionMessageResponse> {
        return this.http.get<DisruptionMessageResponse>(this.getExploreUrl('alerts')).pipe(
            tap((response) => {
                this.allAlertsMemory = response?.messages || [];
            })
        );
    }

    private filterDisruptionMessagesByTime(alerts: DisruptionMessage[], fromTime?: number): DisruptionMessage[] {
        return alerts.filter(({ activePeriodStart, activePeriodEnd }) => {
            if (!fromTime) {
                return true;
            }
            const fromTimeMs = fromTime * 1000;
            const timestampToMs = (timestamp: string | Date) =>
                Temporal.Instant.from(timestamp.toString()).epochMilliseconds;
            if (activePeriodStart && fromTimeMs < timestampToMs(activePeriodStart)) {
                return false;
            }
            if (activePeriodEnd && fromTimeMs > timestampToMs(activePeriodEnd)) {
                return false;
            }
            return true;
        });
    }

    private getDisruptionMessagesForStop(stopId: string, fromTime?: number): DisruptionMessage[] {
        const alertsForStop = this.allAlertsMemory.filter((alert) =>
            alert.informedEntities.some((entity) => entity.stopId === stopId || entity.stopId + ':0' === stopId)
        );

        return this.filterDisruptionMessagesByTime(alertsForStop, fromTime);
    }

    private getDisruptionMessagesForRoute(routeId: string, fromTime?: number): DisruptionMessage[] {
        const alertsForRoute = this.allAlertsMemory.filter((alert) =>
            alert.informedEntities.some((entity) => String(entity.routeId) === routeId)
        );

        const uniqueAlertsForRoute = alertsForRoute.filter(
            (alert, index, self) => index === self.findIndex((a) => a.id === alert.id)
        );

        return this.filterDisruptionMessagesByTime(uniqueAlertsForRoute, fromTime);
    }

    getAllRoutes(): Observable<Route[]> {
        return this.http.get<Route[]>(this.getExploreUrl('routes')).pipe(
            map((routes) => {
                routes.forEach((route) => {
                    route.color = OtpUtil.resolveRouteColor(route);
                    this.allRoutesMemory.set(route.id, route);
                });
                return routes;
            })
        );
    }

    getRoute(routeId: string, fromTime?: number, requestAlerts = true): Observable<DetailedRoute> {
        if (this.routeIdToRouteMemory.has(routeId)) {
            const cachedRoute = this.routeIdToRouteMemory.get(routeId);

            if (this.env.EXPLORE?.ALERTS_ON_ROUTES && requestAlerts && this.network.getCurrentStatus()) {
                const disruptionMessages = this.getDisruptionMessagesForRoute(routeId, fromTime);
                const routeWithDisruptions = {
                    ...cachedRoute,
                    disruptionMessages,
                    disruptionOnRoute: disruptionMessages.length > 0
                };
                return of(routeWithDisruptions);
            }

            return of({
                ...cachedRoute,
                disruptionOnRoute: false,
                disruptionMessages: []
            });
        }

        const inFlight = this.routeIdInFlight.get(routeId);
        if (inFlight) {
            return inFlight;
        }

        const request$ = this.http.get<DetailedRoute>(this.getExploreUrl(`routes/${routeId}`)).pipe(
            switchMap((route) => {
                if (this.env.EXPLORE?.ALERTS_ON_ROUTES && requestAlerts && this.network.getCurrentStatus()) {
                    const disruptionMessages = this.getDisruptionMessagesForRoute(route.id, fromTime);
                    return of({
                        ...route,
                        disruptionOnRoute: disruptionMessages.length > 0,
                        disruptionMessages
                    });
                } else {
                    return of({
                        ...route,
                        disruptionOnRoute: false,
                        disruptionMessages: []
                    });
                }
            }),
            tap((route) => {
                route.color = OtpUtil.resolveRouteColor(route);
                this.routeIdToRouteMemory.set(routeId, route);
            }),
            shareReplay(1),
            finalize(() => this.routeIdInFlight.delete(routeId))
        );
        this.routeIdInFlight.set(routeId, request$);
        return request$;
    }

    getRoutesGroups(): Observable<RouteGroups[]> {
        const url = `${this.env.WMB?.WMB_PUBLIC_API}/v1/route-groups`;

        return this.http.get<RouteGroups[]>(url);
    }

    getAllStops(): Observable<DetailedStop[]> {
        return this.http.get<DetailedStop[]>(this.getExploreUrl('stops')).pipe(
            tap((stops) => {
                stops.forEach((stop) => {
                    this.stopNameToStopLocationsMemory.set(stop.name, stop);
                    this.allStopsMemory.set(stop.id, stop);
                });
            })
        );
    }

    getStopByNameFromMemory(stopName: string, coordinates: LatLng, stopId: number): Stop {
        let currentStop: Stop;
        this.allStopsMemory.forEach((stop, _key) => {
            let condition = stop.id === stopId?.toString();
            if (this.env.JOURNEY_PLANNER?.SEARCH_BY_STOP_ID && condition) {
                currentStop = stop;
            }
            condition = stop.name === stopName && stop.lat === coordinates.lat && stop.lon === coordinates.lng;
            if (this.env.JOURNEY_PLANNER?.SEARCH_BY_COORDINATES && condition) {
                currentStop = stop;
            }
        });

        return currentStop || this.stopNameToStopLocationsMemory.get(stopName);
    }

    getStop(
        stopId: string,
        entityType: string = 'stops',
        fromTime?: number,
        requestAlerts = true
    ): Observable<DetailedStop> {
        if (this.allStopsMemory.has(stopId)) {
            const cachedStop = this.allStopsMemory.get(stopId);

            if (this.env.EXPLORE?.ALERTS_ON_STOPS && requestAlerts && this.network.getCurrentStatus()) {
                const disruptionMessages = this.getDisruptionMessagesForStop(stopId, fromTime);
                const stopWithDisruptions = {
                    ...cachedStop,
                    disruptionMessages,
                    disruptionOnStop: disruptionMessages.length > 0
                };
                return of(stopWithDisruptions);
            }

            return of({
                ...cachedStop,
                disruptionOnStop: false,
                disruptionMessages: []
            });
        }

        return this.http.get<DetailedStop>(this.getExploreUrl(`${entityType}/${stopId}`)).pipe(
            switchMap((stop) => {
                if (this.env.EXPLORE?.ALERTS_ON_STOPS && requestAlerts && this.network.getCurrentStatus()) {
                    const disruptionMessages = this.getDisruptionMessagesForStop(stopId, fromTime);
                    return of({
                        ...stop,
                        disruptionOnStop: disruptionMessages.length > 0,
                        disruptionMessages
                    });
                } else {
                    return of({
                        ...stop,
                        disruptionOnStop: false,
                        disruptionMessages: []
                    });
                }
            }),
            tap((stop) => {
                this.allStopsMemory.set(stopId, stop);
            })
        );
    }

    getPattern(patternId: string): Observable<Pattern> {
        if (this.patternIdToPatternMemory.has(patternId)) {
            return of(this.patternIdToPatternMemory.get(patternId));
        }

        return this.http.get<Pattern>(this.getExploreUrl(`patterns/${patternId}`)).pipe(
            switchMap((pattern) =>
                this.resolvePatternDescription(pattern).pipe(
                    map((description) => {
                        pattern.desc = description;
                        this.patternIdToPatternMemory.set(patternId, pattern);
                        return pattern;
                    })
                )
            )
        );
    }

    getRoutePatterns(routeId: string): Observable<Pattern[]> {
        if (this.routeIdToPatternsMemory.has(routeId)) {
            return of(this.routeIdToPatternsMemory.get(routeId));
        }

        const patterns$ = this.http.get<Pattern[]>(this.getExploreUrl(`routes/${routeId}/patterns`));
        if (this.env.EXPLORE?.PATTERN_DESC_SKIP_FORMATTING) {
            return patterns$;
        }
        return patterns$.pipe(
            switchMap((patterns) =>
                forkJoin(patterns.map((pattern) => this.resolvePatternDescription(pattern))).pipe(
                    map((descriptions) => {
                        for (let i = 0; i < descriptions.length; i++) {
                            patterns[i].desc = descriptions[i];
                        }
                        return patterns;
                    })
                )
            )
        );
    }

    getPatternStops(patternId: string): Observable<Stop[]> {
        if (this.patternIdToStopsMemory.has(patternId)) {
            return of(this.patternIdToStopsMemory.get(patternId));
        }

        return this.http
            .get<Stop[]>(this.getExploreUrl(`patterns/${patternId}/stops`))
            .pipe(tap((stops) => this.patternIdToStopsMemory.set(patternId, stops)));
    }

    getStopsInRadius(latitude: number, longitude: number, radius: number): Observable<Stop[]> {
        if (!latitude || !longitude) {
            return of([]);
        }
        let params = new HttpParams();
        params = params.append('lat', latitude);
        params = params.append('lon', longitude);
        params = params.append('radius', radius);
        params = params.append('maxResults', 20);
        const suffix = this.env.WMB && !this.env.WMB?.DONT_USE_FOR?.includes('STOPS') ? 'stops/nearby' : 'stops';
        return this.http.get<Stop[]>(this.getExploreUrl(suffix), { params: params }).pipe(
            mergeMap((stops: Stop[]) => {
                if (this.env.EXPLORE?.ALERTS_ON_STOPS && this.network.getCurrentStatus()) {
                    const stopsWithAlerts = stops.map((stop) => {
                        const disruptionMessages = this.getDisruptionMessagesForStop(stop.id);
                        return {
                            ...stop,
                            disruptionMessages,
                            disruptionOnStop: disruptionMessages.length > 0
                        };
                    });

                    return of(stopsWithAlerts);
                }
                return of(stops);
            }),
            mergeMap((stops: Stop[]) => {
                if (this.env.OTP_BASE_URL) {
                    const observables = stops.map((stop) => {
                        return this.getStopDescription(stop.id).pipe(
                            map((description) => ({ ...stop, desc: description })),
                            catchError(() => of(stop))
                        );
                    });

                    if (!observables.length) {
                        return of(stops);
                    }

                    return forkJoin(observables);
                }
                return of(stops);
            })
        );
    }

    getStopDescription(stopId: string): Observable<string> {
        if (this.allStopsMemory.has(stopId)) {
            const cachedStop = this.allStopsMemory.get(stopId);
            if (cachedStop.desc) {
                return of(cachedStop.desc);
            }
        }

        const url = this.getExploreUrl(`stops/${stopId}`);
        return this.http.get<Stop>(url).pipe(map((response) => response.desc));
    }

    getStopTimes(options: {
        stopId: string;
        timeRangeSeconds: number;
        fromTime?: number;
        numberOfDepartures?: number;
    }): Observable<StopTime[]> {
        const { stopId, timeRangeSeconds, fromTime, numberOfDepartures } = options;
        let params = new HttpParams();
        if (fromTime) {
            params = params.append('startTime', fromTime);
        }
        params = params.append('timeRange', timeRangeSeconds);
        params = params.append('numberOfDepartures', numberOfDepartures ?? this.env.EXPLORE?.MAX_DEPARTURES ?? 20);
        return this.http.get<StopTime[]>(this.getExploreUrl(`stops/${stopId}/stoptimes`), { params: params });
    }

    getStopTimesOnDate(scheduleParams: ScheduleParams): Observable<StopTime[]> {
        if (this.env.WMB && !this.env.WMB?.DONT_USE_FOR?.includes('STOPS')) {
            let params = new HttpParams();
            const startTime = Temporal.PlainDate.from(scheduleParams.scheduleDate).toZonedDateTime(
                this.appStartupService.getTimezone()
            ).epochSeconds;
            params = params.append('startTime', startTime);
            params = params.append('numberOfDepartures', 200);
            params = params.append('routeId', scheduleParams.pattern.routeId);
            return this.http.get<StopTime[]>(this.getExploreUrl(`stops/${scheduleParams.stopId}/stoptimes`), {
                params
            });
        }

        let params = new HttpParams();
        params = params.append('omitNonPickups', true);
        return this.http.get<StopTime[]>(
            this.getExploreUrl(`stops/${scheduleParams.stopId}/stoptimes/${scheduleParams.scheduleDate}`),
            {
                params
            }
        );
    }

    getPatternScheduleOnStop(scheduleParams: ScheduleParams): Observable<Time[]> {
        return this.getStopTimesOnDate(scheduleParams).pipe(
            map((times) => {
                let resultTimes = [];
                times
                    ?.filter((time) => time.pattern.id === scheduleParams.pattern.id)
                    .forEach((stopTime) => {
                        resultTimes.push(...stopTime.times);
                    });
                const transitionTimeMin = this.appStartupService.getRegion()?.transition_time_min;
                const cutoffHour = transitionTimeMin != null ? Math.floor(transitionTimeMin / 60) : 0;

                const sortTimes = (a: Time, b: Time) => {
                    if (cutoffHour <= 0) {
                        return this.getScheduledDepartureTime(a) - this.getScheduledDepartureTime(b);
                    }
                    const timezone = this.appStartupService.getTimezone();
                    const toKey = (t: Time) => {
                        const ms = this.getScheduledDepartureTime(t);
                        const zdt = Temporal.Instant.fromEpochMilliseconds(ms).toZonedDateTimeISO(timezone);
                        const h = zdt.hour;
                        const min = zdt.minute;
                        const adjH = h < cutoffHour ? h + 24 : h;
                        return adjH * 60 + min;
                    };
                    return toKey(a) - toKey(b);
                };
                resultTimes = resultTimes.sort(sortTimes);
                return resultTimes;
            })
        );
    }

    getUpcomingDepartures(options: {
        stopId: string;
        timeRangeSeconds: number;
        fromTime?: number;
        numberOfDepartures?: number;
    }): Observable<Departure[]> {
        const { stopId, timeRangeSeconds, fromTime, numberOfDepartures } = options;
        const stopTimes$ = this.getStopTimes({ stopId, timeRangeSeconds, fromTime, numberOfDepartures });

        const toInstant = (offsetOrTimeStamp: string | number, serviceDay: number) =>
            typeof offsetOrTimeStamp === 'number'
                ? Temporal.Instant.fromEpochSeconds(serviceDay + offsetOrTimeStamp).toString()
                : offsetOrTimeStamp;

        if (this.env.WMB?.STOPTIMES_WITH_ROUTE_DATA) {
            return stopTimes$.pipe(
                mergeMap((stopTimes) => {
                    if (!stopTimes.length) {
                        return of({ stopTimes: [], routes: [] });
                    }
                    const routesIds = stopTimes.reduce(
                        (acc, stopTime) => (stopTime.pattern?.routeId ? [...acc, stopTime.pattern.routeId] : acc),
                        []
                    );
                    const uniqueRoutesIds = [...new Set(routesIds)];
                    const routesRequests = uniqueRoutesIds.map((routeId) => this.getRoute(routeId, fromTime));

                    return forkJoin(routesRequests).pipe(map((routes) => ({ stopTimes, routes })));
                }),
                map(({ stopTimes, routes }) => {
                    if (!stopTimes.length) {
                        return [];
                    }
                    const routesMap = routes.reduce(
                        (map, route) => {
                            map[route.id] = route;
                            return map;
                        },
                        {} as { [key: string]: any }
                    );
                    return stopTimes
                        .flatMap((stopTime) => {
                            const color = OtpUtil.resolveRouteColor(stopTime.pattern?.routeDetails);
                            const disruptionOnRoute = routesMap[stopTime.pattern?.routeId]?.disruptionOnRoute;

                            return stopTime.times.map((time) => ({
                                time,
                                route: {
                                    ...stopTime.pattern?.routeDetails,
                                    color,
                                    disruptionOnRoute
                                }
                            }));
                        })
                        .sort(
                            (a, b) => this.getScheduledDepartureTime(a.time) - this.getScheduledDepartureTime(b.time)
                        );
                }),
                defaultIfEmpty([])
            );
        }

        return stopTimes$.pipe(
            switchMap((stopTimes) => {
                if (!stopTimes.length) {
                    return of([]);
                }
                return forkJoin(stopTimes.map((stoptime) => this.getRoute(stoptime.pattern.routeId, fromTime))).pipe(
                    map((routes) => {
                        const timeToRouteMap = new Map<Time, DetailedRoute>();
                        for (let i = 0; i < stopTimes.length; i++) {
                            stopTimes[i].times.forEach((time) => {
                                routes[i].disruptionMessages = this.getDisruptionMessagesForRoute(
                                    String(routes[i].id),
                                    options.fromTime
                                );
                                timeToRouteMap.set(time, {
                                    ...routes[i],
                                    mode: stopTimes[i].pattern.routeDetails?.mode
                                });
                            });
                        }
                        const departureTimes: Time[] = stopTimes
                            .map((st) => st.times)
                            .reduce((a, b) => a.concat(b), []);

                        return departureTimes
                            .map((time) => {
                                time.scheduledArrival = toInstant(time.scheduledArrival, time.serviceDay);
                                time.scheduledDeparture = toInstant(time.scheduledDeparture, time.serviceDay);
                                time.realtimeArrival = toInstant(time.realtimeArrival, time.serviceDay);
                                time.realtimeDeparture = toInstant(time.realtimeDeparture, time.serviceDay);
                                return {
                                    time: time,
                                    route: timeToRouteMap.get(time)
                                };
                            })
                            .sort((a, b) =>
                                this.getScheduledDepartureTime(a.time) > this.getScheduledDepartureTime(b.time) ? 1 : -1
                            );
                    })
                );
            }),
            defaultIfEmpty([])
        );
    }

    getTripTimes(tripId: string, fromTime?: number): Observable<Time[]> {
        if (this.env.WMB) {
            let url = this.getExploreUrl('trips/stoptimes') + '?tripId=' + encodeURIComponent(tripId);
            if (fromTime) url += '&from=' + fromTime;
            return this.http.get<Time[]>(url);
        }
        let url = this.getExploreUrl(`trips/${encodeURIComponent(tripId)}/stoptimes`);
        if (fromTime) {
            url += `?from=${encodeURIComponent(fromTime)}`;
        }
        return this.http.get<Time[]>(url);
    }

    getTripGeometry(tripId: string): Observable<Geometry> {
        if (this.env.WMB) {
            return this.http.get<Geometry>(
                this.getExploreUrl('trips/geometry') + '?tripId=' + encodeURIComponent(tripId)
            );
        }
        return this.http.get<Geometry>(this.getExploreUrl(`trips/${encodeURIComponent(tripId)}/geometry`));
    }

    getTripDetails(tripId: string): Observable<TripDetails> {
        return this.http.get<TripDetails>(this.getExploreUrl(`trips/${encodeURIComponent(tripId)}`));
    }

    getPatternGeometry(patternId: string): Observable<Geometry> {
        return this.http.get<Geometry>(this.getExploreUrl(`patterns/${patternId}/geometry`));
    }

    getTripsByRouteId(routeId: string): Observable<TripDetails[]> {
        const url = this.getExploreUrl(`routes/${routeId}/trips`);
        return this.http.get<TripDetails[]>(url).pipe(
            catchError((error) => {
                console.error('Error fetching trips for route:', error);
                return of([]);
            })
        );
    }

    getPlan(payload): Observable<Plan> {
        let params = new HttpParams();
        params = this.appendReluctanceParams(params);
        params = this.appendPlaceParams(params, payload);
        params = params.append('showIntermediateStops', true);
        params = this.appendSearchWindowParams(params, payload);

        const maxResults = this.env.JOURNEY_PLANNER.PLANS_MAX_NUMBER_OF_ITINERARIES;
        if (maxResults) {
            params = params.append('numItineraries', maxResults);
        }

        params = this.appendFilterParams(params, payload);
        params = this.appendDateTimeParams(params, payload);
        params = this.appendEtisParams(params, payload, maxResults);

        this.analyticsService.eventTrack('search', {
            category: 'journey-planner',
            name: payload.fromPlace + ' - ' + payload.toPlace
        });

        params = this.balanceJourneyCountParams(params, maxResults);

        return this.http
            .get<Plan>(this.getJourneyUrl('plan'), { params })
            .pipe(map((result) => ({ ...result, plan: this.mapWmbPlanLegs(result.plan) }) as Plan));
    }

    private appendReluctanceParams(params: HttpParams): HttpParams {
        if (this.env.JOURNEY_PLANNER?.WALK_RELUCTANCE) {
            params = params.append('walkReluctance', this.env.JOURNEY_PLANNER.WALK_RELUCTANCE);
        }
        if (this.env.JOURNEY_PLANNER?.WAIT_RELUCTANCE) {
            params = params.append('waitReluctance', this.env.JOURNEY_PLANNER.WAIT_RELUCTANCE);
        }
        return params;
    }

    private appendPlaceParams(params: HttpParams, payload: any): HttpParams {
        if (this.env.APP_NAME === 'vastmanland-app') {
            params = params.append('fromPlace', payload.fromPlace).append('toPlace', payload.toPlace);
            if (payload.fromPointType) params = params.append('fromPointType', payload.fromPointType);
            if (payload.toPointType) params = params.append('toPointType', payload.toPointType);
        } else if (this.env.IS_NEW_AFC) {
            const formatPlace = (p: string | LatLng) => (typeof p === 'string' ? p : `${p.lat},${p.lng}`);
            const { fromPlace, toPlace, fromPointType, toPointType } = payload;
            params = params.append('fromPlace', formatPlace(fromPlace)).append('toPlace', formatPlace(toPlace));
            if (fromPointType) params = params.append('fromPointType', fromPointType);
            if (toPointType) params = params.append('toPointType', toPointType);
        } else {
            params = params.append('fromPlace', `${payload.fromPlace.lat},${payload.fromPlace.lng}`);
            params = params.append('toPlace', `${payload.toPlace.lat},${payload.toPlace.lng}`);
        }
        return params;
    }

    private appendSearchWindowParams(params: HttpParams, payload: any): HttpParams {
        const searchWindowHours = this.env.JOURNEY_PLANNER.PLANS_SEARCH_WINDOW_HOURS;
        if (!searchWindowHours) return params;

        let searchWindowSec: number;
        if (payload.expandSearchWindow && this.env.JOURNEY_PLANNER?.EXPAND_SEARCH_FOR_LIMITED_TRIPS) {
            searchWindowSec = this.calculateSecondsToTheEndTransportOfDay(payload);
        } else if (this.env.APP_NAME === 'gotland') {
            searchWindowSec = this.calculateSecondsToTheEndTransportOfDay(payload);
        } else {
            searchWindowSec = 60 * 60 * searchWindowHours;
        }
        return params.append('searchWindow', searchWindowSec);
    }

    private appendFilterParams(params: HttpParams, payload: any): HttpParams {
        if (payload.filters?.routes?.length > 0) {
            params = params.append('whiteListedRoutes', payload.filters.routes);
        }
        if (this.env.JOURNEY_PLANNER?.FILTERS?.TRANSPORT_MODE && payload.filters?.transportModesValue) {
            params = params.append('selectedMeansOfTransport', payload.filters.transportModesValue);
            params = params.append('selectedType', 'TRANSPORT_MODES');
        }
        if (this.env.JOURNEY_PLANNER?.FILTERS?.SELECTORS) {
            for (const selector of this.env.JOURNEY_PLANNER.FILTERS.SELECTORS) {
                if (payload.filters?.[selector.QUERY_PARAMETER]) {
                    params = params.append(selector.QUERY_PARAMETER, payload.filters[selector.QUERY_PARAMETER]);
                }
            }
        }
        return params;
    }

    private appendDateTimeParams(params: HttpParams, payload: any): HttpParams {
        if (!payload.searchDateAndTime) return params;

        if (payload.searchDateAndTime.arrive !== undefined) {
            params = params.append('arriveBy', payload.searchDateAndTime.arrive);
        }
        if (this.env.WMB && !this.env.WMB?.DONT_USE_FOR?.includes('PLAN')) {
            const dateTime = Temporal.PlainDate.from(payload.searchDateAndTime.date)
                .toZonedDateTime({
                    plainTime: payload.searchDateAndTime.time,
                    timeZone: this.appStartupService.getTimezone()
                })
                .toInstant()
                .toString();
            params = params.append('datetime', dateTime);
        } else {
            params = params.append('time', payload.searchDateAndTime.time);
            params = params.append('date', payload.searchDateAndTime.date);
        }
        return params;
    }

    private appendEtisParams(params: HttpParams, payload: any, maxResults: number): HttpParams {
        if (this.env.JOURNEY_PLANNER?.PROVIDER !== 'ETIS') return params;

        // default datetime is now when not given by filter
        if (!payload?.searchDateAndTime) {
            params = params.append('datetime', Temporal.Now.instant().toString());
        }

        const loadMore = payload?.loadMore;

        // include optimal journey
        const includeOptimalJourney = this.env?.JOURNEY_PLANNER?.SHOW_LAST_DEPARTED_JOURNEYS?.INCLUDE_OPTIMAL_JOURNEY;
        if (includeOptimalJourney) {
            params = params.set('includeOptimalJourney', includeOptimalJourney);
        }

        // if were in arriveBy mode use different before/after amount of journeys
        if (payload.searchDateAndTime?.arrive) {
            params = this.appendArriveByEtisParams(params, payload, maxResults);
        } else {
            params = this.appendDepartByEtisParams(params);
        }

        if (loadMore) {
            params = params.delete('includeOptimalJourney');
        }
        return params;
    }

    private appendArriveByEtisParams(params: HttpParams, payload: any, maxResults: number): HttpParams {
        const { loadMore } = payload;
        const config = this.env?.JOURNEY_PLANNER?.SHOW_LAST_DEPARTED_JOURNEYS;

        // For initial query — keep some buffer +2 in addition to setting value
        if (config?.JOURNEYS_BEFORE_ARRIVE_BY > 0 && !loadMore) {
            params = params.set('numberOfJourneysBeforeDatetime', config.JOURNEYS_BEFORE_ARRIVE_BY + 2);
        }

        // When load more
        if (config?.JOURNEYS_BEFORE_ARRIVE_BY_MORE > 0 && loadMore === 'BEFORE') {
            params = params.set('numberOfJourneysBeforeDatetime', maxResults).delete('numItineraries');
        }
        if (config?.JOURNEYS_AFTER_ARRIVE_BY_MORE > 0 && loadMore === 'AFTER') {
            params = params.delete('numberOfJourneysBeforeDatetime');
        }
        return params;
    }

    private appendDepartByEtisParams(params: HttpParams): HttpParams {
        const numberOfJourneysBefore = this.env?.JOURNEY_PLANNER?.SHOW_LAST_DEPARTED_JOURNEYS?.MAX_JOURNEYS;
        if (numberOfJourneysBefore > 0) {
            params = params.set('numberOfJourneysBeforeDatetime', numberOfJourneysBefore);
        }
        return params;
    }

    private balanceJourneyCountParams(params: HttpParams, maxResults: number): HttpParams {
        const before = Number(params.get('numberOfJourneysBeforeDatetime')) || 0;
        const after = Number(params.get('numItineraries')) || 0;
        const optimal = params.get('includeOptimalJourney') === 'true' ? 1 : 0;
        let updatedBefore = before;
        let updatedAfter = after;

        while (updatedBefore + updatedAfter + optimal >= maxResults) {
            if (updatedBefore >= updatedAfter) {
                updatedBefore--;
            } else {
                updatedAfter--;
            }
        }
        return params.set('numberOfJourneysBeforeDatetime', updatedBefore).set('numItineraries', updatedAfter);
    }

    private mapWmbPlanLegs(plan: Plan['plan']): Plan['plan'] {
        if (!this.env.WMB || !plan?.itineraries || this.env.WMB?.DONT_USE_FOR?.includes('PLAN')) {
            return plan;
        }

        // The coordinates of the stops do not always match the coordinates of the stops in the search results.
        // We remove segments with inaccuracies.
        const unionTransportModes = new Set([
            TransportMode.Bus,
            TransportMode.Ferry,
            TransportMode.Rail,
            TransportMode.Tram,
            TransportMode.Trolleybus
        ]);

        return {
            ...plan,
            itineraries: plan.itineraries.map((itinerary: Itinerary) => ({
                ...itinerary,
                legs: itinerary.legs.map((leg: Leg) => {
                    if (!leg.tripId && unionTransportModes.has(leg.mode)) {
                        leg.tripId = leg.from.stopId + leg.to.stopId;
                    }
                    return leg;
                })
            }))
        };
    }

    getMeansOfTransport(): Observable<any> {
        return this.http.get(this.getJourneyUrl('meansOfTransport'));
    }

    getStopWithWalkingDistance(stopId: string, currentPosition: LatLng, fromTime?: number): Observable<DetailedStop> {
        return this.getStop(stopId, 'stops', fromTime).pipe(
            map((stop) => ({
                ...stop,
                dist: getDistance(
                    {
                        latitude: stop.lat,
                        longitude: stop.lon
                    },
                    {
                        latitude: currentPosition?.lat,
                        longitude: currentPosition?.lng
                    }
                )
            }))
        );
    }

    getRoutePatternStops(routeId: string, getStopDescription: boolean = true): Observable<RoutePatternStops[]> {
        return this.getRoutePatterns(routeId).pipe(
            switchMap((patterns) =>
                forkJoin(patterns.map((pattern) => this.getPatternStops(pattern.id))).pipe(
                    switchMap((stops) =>
                        forkJoin(patterns.map((pattern) => this.getRoute(pattern.routeId))).pipe(
                            switchMap((routes) => {
                                const data: RoutePatternStops[] = [];
                                for (let i = 0; i < patterns.length; i++) {
                                    data.push({
                                        pattern: patterns[i],
                                        stops: stops[i],
                                        route: routes[i]
                                    });
                                }
                                return of(data);
                            }),
                            switchMap((data: RoutePatternStops[]) => {
                                if (!getStopDescription) {
                                    return of(data);
                                }
                                const observables = data.flatMap((item) =>
                                    item.stops.map((stop) =>
                                        this.getStopDescription(stop.id).pipe(
                                            map((description) => {
                                                stop.desc = description;
                                                return stop;
                                            }),
                                            catchError(() => of(stop))
                                        )
                                    )
                                );

                                return observables.length ? forkJoin(observables).pipe(map(() => data)) : of(data);
                            })
                        )
                    )
                )
            )
        );
    }

    private resolvePatternDescription(pattern: Pattern): Observable<string> {
        if (this.patternIdToPatternDescriptionMemory.has(pattern?.id)) {
            return of(this.patternIdToPatternDescriptionMemory.get(pattern?.id));
        }
        return this.getPatternStops(pattern?.id).pipe(
            map((stops) => {
                const desc = OtpUtil.getPatternDescription(pattern, stops);
                this.patternIdToPatternDescriptionMemory.set(pattern?.id, desc);
                return desc;
            })
        );
    }

    getSearchRecentlyJourneys(): SearchRecentlyJourney[] {
        return JsonUtil.parse<SearchRecentlyJourney[]>(
            localStorage.getItem(searchRecentlyJourneysKey),
            Array.isArray,
            []
        );
    }

    addSearchRecentlyJourneys(result: SearchRecentlyJourney) {
        try {
            const prevValue = this.getSearchRecentlyJourneys();
            const notRepeatedItems = prevValue.filter(
                (j) =>
                    !(
                        isEqual(j.origin.coordinates, result.origin.coordinates) &&
                        isEqual(j.destination.coordinates, result.destination.coordinates)
                    )
            );

            localStorage.setItem(searchRecentlyJourneysKey, JSON.stringify([result, ...notRepeatedItems].slice(0, 3)));
        } catch (err) {
            console.error('OpenTripPlaner.addSearchRecentlyJourney', err);
        }
    }

    clearSearchRecentlyJourneys() {
        localStorage.removeItem(searchRecentlyJourneysKey);
    }

    public getRoutes() {
        return this.allRoutesMemory;
    }

    public getStops() {
        return this.allStopsMemory;
    }

    private calculateSecondsToTheEndTransportOfDay(payload): number {
        const endOfTransportDay = Temporal.PlainTime.from('03:59:59');

        let requestTime: Temporal.PlainTime;
        try {
            requestTime = payload?.searchDateAndTime?.time
                ? Temporal.PlainTime.from(payload.searchDateAndTime.time)
                : Temporal.Now.plainTimeISO();
        } catch (error) {
            requestTime = Temporal.Now.plainTimeISO();
        }
        const timeDifference = requestTime.until(endOfTransportDay, { largestUnit: 'seconds' }).seconds;
        if (timeDifference < 0) {
            const secondsInDay = 86400;
            return timeDifference + secondsInDay;
        } else {
            return timeDifference;
        }
    }

    secondsToInstant(dateValue: number | string): Temporal.Instant {
        return typeof dateValue === 'string'
            ? Temporal.Instant.from(dateValue)
            : Temporal.Instant.fromEpochSeconds(dateValue);
    }

    transformTripDetailsTimeToStopMarkers(times: TripDetailsTime[]): StopMarker[] {
        const getTime = (time: number | string, isLast: boolean): string | undefined =>
            isLast ? this.dateFormatter(this.secondsToInstant(time), this.TIME_FORMAT) : undefined;
        return (
            times?.map((time, i) => {
                const isLast = times.length - 1 === i;
                const departureClockTime = getTime(time.scheduledDeparture, !isLast);
                const arrivalClockTime = getTime(time.scheduledArrival, isLast);
                return {
                    name: time.stop.name,
                    code: time.stop.code,
                    dist: time.stop.dist,
                    departureClockTime,
                    arrivalClockTime,
                    latlng: { lat: time.stop.lat, lng: time.stop.lon } as LatLng
                };
            }) ?? []
        );
    }

    formattedSecondsToClockString(time: number | string): string {
        return this.dateFormatter(this.secondsToInstant(time), this.TIME_FORMAT);
    }

    private dateFormatter(date: Temporal.Instant, format: string = ''): string {
        const milliseconds = date.toZonedDateTimeISO(this.appStartupService.getTimezone()).epochMilliseconds;
        return this.datePipe.transform(milliseconds, format, this.appStartupService.getTimezoneOffset);
    }

    getScheduledDepartureTime(time: Time): number {
        if (time.realtimeDeparture && typeof time.realtimeDeparture === 'string') {
            return this.getZonedMilliseconds(time.realtimeDeparture);
        } else if (time.scheduledDeparture && typeof time.scheduledDeparture === 'string') {
            return this.getZonedMilliseconds(time.scheduledDeparture);
        } else if (typeof time.scheduledDeparture === 'number') {
            return this.getZonedMilliseconds(time.serviceDay + time.scheduledDeparture);
        }
    }

    getZonedMilliseconds = (time: number | string): number => {
        return typeof time === 'string'
            ? Temporal.Instant.from(time).epochMilliseconds
            : Temporal.Instant.fromEpochSeconds(time).epochMilliseconds;
    };

    getCurrentSeconds(): number {
        return Temporal.Now.instant().epochSeconds;
    }

    getDefaultRouteFilters(filters: JourneyPlannerFilter): Observable<JourneyPlannerFilter> {
        return new Observable<JourneyPlannerFilter>((observer) => {
            const defaultValue: any = getDefaultValue(this.env.JOURNEY_PLANNER?.FILTERS);
            const currentValue: any = { ...filters };
            const maxWalkDistanceSelector = this.env.JOURNEY_PLANNER?.FILTERS?.SELECTORS.find(
                (selector) => selector.TITLE_KEY === 'JOURNEY_PLANNER_FILTER_MAX_WALK_DISTANCE_TITLE'
            );
            const initialValueMaxWalkDistance = -1;

            if (this.env.JOURNEY_PLANNER?.FILTERS?.TRANSPORT_MODE) {
                this.getMeansOfTransport()
                    .pipe(take(1))
                    .subscribe((meansOfTransport) => {
                        const transportModes = meansOfTransport.transportModes;

                        const transportModesFormValue = meansOfTransport.transportModes
                            .filter((mode) => mode.defaultChecked)
                            .map((mode) => mode.translationTag);

                        filters = {
                            ...filters,
                            transportModes: transportModesFormValue
                        };

                        filters = {
                            ...filters,
                            transportModesValue: transportModes
                                .filter((mode) => filters.transportModes.includes(mode.translationTag))
                                .map((mode) => mode.id)
                                .reduce((accumulator, currentValue) => accumulator + currentValue, 0)
                        };

                        Object.keys(defaultValue).forEach((key) => (defaultValue[key] = [defaultValue[key]]));
                        Object.keys(currentValue).forEach((key) => (currentValue[key] = [currentValue[key]]));

                        if (currentValue.maxWalkDistance) {
                            const currentValueIndex = maxWalkDistanceSelector?.VALUES.findIndex(
                                (el) => el.VALUE === currentValue.maxWalkDistance[0]
                            );

                            if (currentValueIndex >= 0 || currentValueIndex !== undefined) {
                                currentValue.maxWalkDistance = [currentValueIndex];
                            }

                            if (currentValue.maxWalkDistance[0] === initialValueMaxWalkDistance) {
                                currentValue.maxWalkDistance = [maxWalkDistanceSelector.INIT_VALUE];
                            }
                        }

                        observer.next(filters);
                    });
            } else {
                observer.next(filters);
            }
        });
    }
}
